/** Native observation of bindings and runtime state; transport and console capture stay in the host. */
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { getTransitionManager } from '../../core/TransitionManager';
import { Properties } from '../../behaviors/properties';
import { Timeline } from '../../behaviors/timeline';
import { Pages } from '../../behaviors/pages';
import { Text } from '../../behaviors/text';
import { BehaviorGraph } from '../../behaviors/behaviorgraph/behaviorgraph';
import { NullEngine } from '@babylonjs/core/Engines/nullEngine';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { BindingDiagnostic } from '../../behaviors/tracebehavior';
import { getBehaviorSchema } from '../../scenedata/schemas';

export function inspectRuntimeBindings(objects: readonly SceneObject[], limit?: number): BindingDiagnostic[] {
    if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 1)) throw new Error('Diagnostic limit must be a positive integer');
    const count = limit ?? Infinity;
    const diagnostics: BindingDiagnostic[] = [];
    for (const object of objects) for (const behavior of object.getBehaviors()) {
        diagnostics.push(...behavior.getBindingDiagnostics(count - diagnostics.length));
        if (diagnostics.length >= count) return diagnostics;
    }
    return diagnostics;
}

export function listRuntimeActions(object: SceneObject) {
    return object.getBehaviors().flatMap(behavior => {
        const behaviorType = behavior.getData()['@type'];
        return (getBehaviorSchema(behaviorType)?.actions ?? []).map(action => ({ behaviorType, action: { ...action } }));
    });
}

/** Heuristic only: literal numeric labels matching a mutable property may go stale. */
export function findStaticReadouts(trace: TraceScene, limit?: number) {
    if (limit !== undefined && (!Number.isSafeInteger(limit) || limit < 1)) throw new Error('Readout limit must be a positive integer');
    const values: { owner: string; name: string; value: number }[] = [];
    for (const object of trace.getSceneObjects()) {
        const properties = object.getBehavior(Properties);
        for (const name of properties?.getPropertyNames() ?? []) {
            const value = properties!.getValue(name);
            if (typeof value === 'number' && Number.isFinite(value)) values.push({ owner: object.name, name, value });
        }
    }
    const found: { object: string; text: string; shows: string }[] = [];
    for (const object of trace.getSceneObjects()) {
        const authored = object.getBehavior(Text)?.getData().text;
        if (typeof authored !== 'string' || authored.includes('{') || onPage(object)) continue;
        const numbers = [...authored.matchAll(/-?\d+(?:\.\d+)?/g)].map(match => Number(match[0]));
        const match = values.find(entry => numbers.some(number => Math.abs(number - entry.value) < 1e-9));
        if (!match) continue;
        found.push({ object: object.getPath(), text: authored, shows: `${match.owner}.${match.name} = ${match.value}` });
        if (limit !== undefined && found.length >= limit) break;
    }
    return found;
}

// Page captions change with their page; shared content can still be stale.
function onPage(object: SceneObject): boolean {
    for (let at = object, parent = at.getParent(); parent; at = parent, parent = parent.getParent()) {
        if (parent.getBehavior(Pages)?.getPageNodes().includes(at.transform)) return true;
    }
    return false;
}

export function runtimeSnapshot(objects: readonly SceneObject[]) {
    return objects.map(object => {
        const transform = object.transform;
        transform.computeWorldMatrix(true);
        const world = transform.getWorldMatrix();
        const rise = (axis: Vector3) => {
            const direction = Vector3.TransformNormal(axis, world).normalize();
            return Math.asin(Math.max(-1, Math.min(1, direction.y))) * 180 / Math.PI;
        };
        const properties = object.getBehavior(Properties), timeline = object.getBehavior(Timeline), pages = object.getBehavior(Pages);
        return {
            path: object.getPath(),
            transform: {
                position: transform.position.asArray(), worldPosition: transform.getAbsolutePosition().asArray(),
                rotation: transform.rotationQuaternion?.asArray() ?? transform.rotation.asArray(), scale: transform.scaling.asArray(),
                slopeDeg: { x: rise(Vector3.Right()), z: rise(Vector3.Forward()) },
            },
            properties: properties ? structuredClone(Object.fromEntries(properties.getPropertyNames().map(name => [name, properties.getValue(name)]))) : {},
            timeline: timeline ? { time: timeline.getTime(), playing: timeline.isPlaying() } : undefined,
            page: pages ? { index: pages.getActivePageIndex(), count: pages.getPageCount() } : undefined,
        };
    });
}

/**
 * Put a disposable copy on one explicit clock, starting at 0: system values (`time`), transitions, `after` timers and Babylon's
 * animation clips all read it. Returns the clock; advancing `nowMs` and rendering is stepping scene time. Call it once, before play.
 */
export function useExplicitClock(trace: TraceScene): { nowMs: number } {
    const clock = { nowMs: 0 };
    trace.getSystemValues().setClock(() => clock.nowMs);
    const scene = trace.getBabylonScene();
    getTransitionManager(scene).setClock(() => clock.nowMs);
    // Babylon times animation groups by the wall clock between renders, so a session stepped faster than real time would play them
    // slow. Hand every step the explicit time since the last one instead (a lockstep engine passes its own fixed step).
    const animate = scene._animate.bind(scene);
    let animatedMs = 0;
    scene._animate = customDeltaTime => {
        const elapsed = clock.nowMs - animatedMs;
        animatedMs = clock.nowMs;
        animate(customDeltaTime ?? elapsed);
    };
    return clock;
}

/** Internal to a disposable runtime: the explicit clock spans every sequence step. */
export async function stepRuntime(trace: TraceScene, objects: readonly SceneObject[], durationMs: number, clock: { nowMs: number }) {
    const scene = trace.getBabylonScene(), engine = scene.getEngine();
    if (!(engine instanceof NullEngine)) throw new Error('Native fixed stepping requires NullEngine');
    const before = runtimeSnapshot(objects);
    const graphs = () => objects.flatMap(object => (object.getBehavior(BehaviorGraph)?.getRuntimeDiagnostics() ?? []).map(d => ({ object: object.getPath(), ...d })));
    const graphDiagnosticsBefore = graphs();
    const bindingDiagnosticsBefore = inspectRuntimeBindings(objects);
    const moving = new Set<string>(), changed = new Set<string>();
    let previous = before, elapsedMs = 0, delta = 0;
    const frames = Math.ceil(durationMs / (1000 / 60));
    const descriptor = Object.getOwnPropertyDescriptor(engine, 'getDeltaTime');
    Object.defineProperty(engine, 'getDeltaTime', { configurable: true, value: () => delta });
    try {
        for (let i = 0; i < frames; i++) {
            delta = i === frames - 1 ? durationMs - elapsedMs : 1000 / 60;
            clock.nowMs += delta;
            scene.render();
            await Promise.resolve();
            elapsedMs += delta;
            const current = runtimeSnapshot(objects);
            for (let j = 0; j < current.length; j++) {
                if (JSON.stringify(current[j].transform) !== JSON.stringify(previous[j].transform)) moving.add(current[j].path);
                if (JSON.stringify(current[j]) !== JSON.stringify(previous[j])) changed.add(current[j].path);
            }
            previous = current;
        }
    } finally {
        if (descriptor) Object.defineProperty(engine, 'getDeltaTime', descriptor);
        else Reflect.deleteProperty(engine, 'getDeltaTime');
    }
    return {
        mode: 'fixed-step' as const, requestedMs: durationMs, elapsedMs, frames,
        graphDiagnosticsBefore, graphDiagnostics: graphs(), before, after: previous, movingObjects: [...moving], changedObjects: [...changed],
        bindingDiagnosticsBefore, bindingDiagnosticsAfter: inspectRuntimeBindings(objects),
    };
}
